import React, { useState } from 'react';
import { getFirestore, collection, addDoc, GeoPoint } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Composants UI
import Modal from '../ui/Modal/Modal';
import Button from '../ui/Button/Button';
import Input from '../ui/Input/Input';
import TextArea from '../ui/TextArea/TextArea';
import Autocomplete from "react-google-autocomplete";
import './CreateShopForm.css';

const CreateShopForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    titre: '',
    address: '',
    phoneNumber: '',
    email: '',
    coordinates: [0, 0], // Garder [lat, lon]
    selectedDays: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    },
    schedule: {
      continuous: false,
      morning: { open: '', close: '' },
      afternoon: { open: '', close: '' },
      fullDay: { open: '', close: '' }
    }
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      selectedDays: {
        ...prev.selectedDays,
        [day]: !prev.selectedDays[day]
      }
    }));
  };

  const handleScheduleChange = (period, type, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [period]: {
          ...prev.schedule[period],
          [type]: value
        }
      }
    }));
  };

  const toggleContinuous = () => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        continuous: !prev.schedule.continuous
      }
    }));
  };

  const onPlaceSelected = (place) => {
    if (!place || !place.geometry || !place.geometry.location) {
      console.error("Adresse sélectionnée invalide ou sans géométrie.");
      setFormData(prev => ({ ...prev, coordinates: [0, 0], address: '' }));
      return;
    }
    console.log("Place sélectionnée:", place);
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setFormData(prev => ({
      ...prev,
      address: place.formatted_address || prev.address,
      coordinates: [lat, lng]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (!formData.address || (formData.coordinates[0] === 0 && formData.coordinates[1] === 0)) {
      console.warn("Adresse non valide ou non géocodée. La boutique sera créée sans coordonnées GPS.");
    }

    try {
      let imageUrl = '';
      if (imageFile) {
        const storage = getStorage();
        const timestamp = Date.now();
        const storageRef = ref(storage, `shops/${timestamp}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const db = getFirestore();
      const hours = {};
      
      Object.entries(formData.selectedDays).forEach(([day, isSelected]) => {
        if (isSelected) {
          if (formData.schedule.continuous) {
            hours[day] = {
              morning: {
                open: formData.schedule.fullDay.open,
                close: formData.schedule.fullDay.close
              }
            };
          } else {
            hours[day] = {
              morning: {
                open: formData.schedule.morning.open,
                close: formData.schedule.morning.close
              },
              afternoon: {
                open: formData.schedule.afternoon.open,
                close: formData.schedule.afternoon.close
              }
            };
          }
        } else {
          hours[day] = null;
        }
      });

      let geoPoint = null;
      if (Array.isArray(formData.coordinates) && formData.coordinates.length === 2 && (formData.coordinates[0] !== 0 || formData.coordinates[1] !== 0)) {
        geoPoint = new GeoPoint(formData.coordinates[0], formData.coordinates[1]);
      } else {
        console.warn(`Coordonnées invalides pour ${formData.address}, enregistrement sans GeoPoint.`);
      }

      await addDoc(collection(db, 'shops'), {
        titre: formData.titre,
        address: formData.address,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        coordinates: geoPoint,
        description: '',
        imageUrl,
        hours,
        dateCreation: new Date().toISOString()
      });

      onSuccess();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors de la création de la boutique');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal 
      title="Ajouter une boutique"
      onClose={onClose}
      className="shop-form"
    >
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}>
        <div className="modal-sections">
          <div className="form-section">
          <div className="form-group">
              <Input
                label="Nom de la boutique"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
                placeholder="Exemple : L'UNION - HYPE VAP"
              required
            />
          </div>

          <div className="form-group address-group">
            <label>Adresse</label>
            <Autocomplete
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              style={{ width: '100%' }}
              onPlaceSelected={onPlaceSelected}
              options={{
                types: ["address"],
                componentRestrictions: { country: "fr" },
                fields: ["formatted_address", "geometry.location"]
              }}
              placeholder="Commencez à taper l'adresse..."
              className="address-autocomplete-input"
            />
            <p className="field-info">
              L'adresse doit être complète pour permettre la géolocalisation de la boutique.
              <br />
              Elle sera affichée dans l'application mobile et permettra aux utilisateurs de se rendre à la boutique.
            </p>
            {formData.coordinates[0] !== 0 && formData.coordinates[1] !== 0 && (
              <small className="coordinates-info">
                Coordonnées GPS trouvées : {formData.coordinates[0].toFixed(6)}, {formData.coordinates[1].toFixed(6)}
              </small>
            )}
          </div>

          <div className="form-group">
              <Input
                type="tel"
                label="Numéro de téléphone"
                name="phoneNumber"
                value={formData.phoneNumber}
              onChange={handleChange}
                placeholder="01 02 03 04 05"
            />
              <p className="field-info">
                Format requis : 01 02 03 04 05
                <br />
                Ce numéro sera affiché dans l'application mobile.
              </p>
          </div>

          <div className="form-group">
              <Input
                type="email"
                label="Email"
                name="email"
                value={formData.email}
              onChange={handleChange}
                placeholder="exemple@email.com"
            />
              <p className="field-info">
                L'adresse email est optionnelle et ne sera pas affichée dans l'application mobile.
              </p>
          </div>

          <div className="form-group horaires-section">
            <label>Jours d'ouverture</label>
            <div className="days-selection">
              {Object.entries(formData.selectedDays).map(([day, isSelected]) => (
                <label key={day} className="day-checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleDayToggle(day)}
                  />
                  <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                </label>
              ))}
            </div>

            {Object.values(formData.selectedDays).some(day => day) && (
              <div className="schedule-config">
                <div className="horaires-type">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={formData.schedule.continuous}
                      onChange={toggleContinuous}
                    />
                    <span>Horaires en continu</span>
                  </label>
                </div>

                {formData.schedule.continuous ? (
                  <div className="periode">
                    <span>Horaires</span>
                    <input
                      type="time"
                      value={formData.schedule.fullDay.open}
                      onChange={(e) => handleScheduleChange('fullDay', 'open', e.target.value)}
                      className="time-input"
                    />
                    <span>à</span>
                    <input
                      type="time"
                      value={formData.schedule.fullDay.close}
                      onChange={(e) => handleScheduleChange('fullDay', 'close', e.target.value)}
                      className="time-input"
                    />
                  </div>
                ) : (
                  <>
                    <div className="periode">
                      <span>Matin</span>
                      <input
                        type="time"
                        value={formData.schedule.morning.open}
                        onChange={(e) => handleScheduleChange('morning', 'open', e.target.value)}
                        className="time-input"
                      />
                      <span>à</span>
                      <input
                        type="time"
                        value={formData.schedule.morning.close}
                        onChange={(e) => handleScheduleChange('morning', 'close', e.target.value)}
                        className="time-input"
                      />
                    </div>
                    <div className="periode">
                      <span>Après-midi</span>
                      <input
                        type="time"
                        value={formData.schedule.afternoon.open}
                        onChange={(e) => handleScheduleChange('afternoon', 'open', e.target.value)}
                        className="time-input"
                      />
                      <span>à</span>
                      <input
                        type="time"
                        value={formData.schedule.afternoon.close}
                        onChange={(e) => handleScheduleChange('afternoon', 'close', e.target.value)}
                        className="time-input"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            </div>
          </div>

          <div className="image-section">
            <div className="form-group">
              <label>Image de la boutique</label>
              <div className="shop-image-upload-zone">
                {!imagePreview ? (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="shop-image-input"
                      id="shop-image-input"
                    />
                    <label htmlFor="shop-image-input" className="shop-upload-label">
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span>Glisser une image ou cliquer pour sélectionner</span>
                    </label>
                  </>
                ) : (
                  <div className="shop-image-preview-container">
                    <img src={imagePreview} alt="Prévisualisation" />
            <button 
              type="button" 
                      className="shop-remove-image"
                      onClick={handleImageRemove}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
              </div>
              <p className="field-info">
                L'ajout d'une image est optionnel.
                <br />
                Les images des boutiques ne sont pas affichées dans l'application mobile.
              </p>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <Button 
            variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            type="button"
            >
              Annuler
          </Button>
          <Button
            variant="primary"
              type="submit" 
              disabled={isLoading}
            >
            {isLoading ? 'Création en cours...' : 'Créer la boutique'}
          </Button>
          </div>
        </form>
    </Modal>
  );
};

export default CreateShopForm; 